/**
 * Транспорт API клиента: один метод `call(имя, тело)` для всех эндпоинтов из `@newfungame/contracts`.
 * Два варианта — HTTP (`createHttpTransport`) и фейковый в памяти (`createFakeTransport`, ./fake.ts) для разработки
 * интерфейса без сервера. Синхронизацию (очередь без сети, откат) строит поверх I2.
 */
import type { ApiRequest, ApiResponse, EndpointName } from "@newfungame/contracts";
import { ApiError, ApiErrorBody, endpoints, MODE_HEADER, type GameMode } from "@newfungame/contracts";

/** Дополнительные параметры вызова. */
export interface CallOptions {
  /** Заголовок `Idempotency-Key` (S3): повтор с тем же ключом не списывает второй раз. */
  idempotencyKey?: string;
}

export interface ApiTransport {
  call<N extends EndpointName>(name: N, req: ApiRequest<N>, opts?: CallOptions): Promise<ApiResponse<N>>;
}

/** Транспорт по HTTP к своему серверу (тот же origin, cookie-сессия). */
export function createHttpTransport(base = "", mode: () => GameMode = () => "main"): ApiTransport {
  return {
    async call(name, req, opts) {
      const ep = endpoints[name];
      const res = await fetch(`${base}${ep.path}`, {
        method: ep.method,
        credentials: "include",
        headers: {
          ...(ep.method === "POST" ? { "content-type": "application/json" } : {}),
          ...(mode() === "beta" ? { [MODE_HEADER]: "beta" } : {}),
          ...(opts?.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : {}),
        },
        body: ep.method === "POST" ? JSON.stringify(req) : null,
      });
      const json: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const err = ApiErrorBody.safeParse(json);
        throw err.success
          ? new ApiError(err.data.error.code, err.data.error.message)
          : new ApiError("internal", `HTTP ${res.status}`);
      }
      return ep.response.parse(json) as ApiResponse<typeof name>;
    },
  };
}
